/**
 * DynamicFormRenderer Component
 * Renders forms dynamically based on JSON schema from SecureForms system
 * 
 * Supports:
 * - Multiple field types (text, email, tel, number, select, textarea, checkbox, radio, date, file)
 * - Form sections with titles and descriptions
 * - Field validation (required, patterns, min/max, custom messages)
 * - Conditional field visibility
 * - Multi-step forms with progress indicators
 * - Prefilled data
 */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormField from './FormField';
import styles from './DynamicFormRenderer.module.css';

const DynamicFormRenderer = ({
    formSchema,
    prefillData = {},
    onSubmit,
    onCancel,
    submitting = false,
    className = ''
}) => {
    const [formData, setFormData] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [touchedFields, setTouchedFields] = useState({});

    // Parse schema if it's a string
    const parsedSchema = useMemo(() => {
        if (!formSchema) {
            console.error('DynamicFormRenderer: formSchema is null or undefined');
            return null;
        }
        
        console.log('DynamicFormRenderer: Received formSchema:', formSchema);
        console.log('DynamicFormRenderer: formSchema type:', typeof formSchema);
        
        if (typeof formSchema === 'string') {
            try {
                const parsed = JSON.parse(formSchema);
                console.log('DynamicFormRenderer: Successfully parsed string schema:', parsed);
                return parsed;
            } catch (e) {
                console.error('DynamicFormRenderer: Failed to parse form schema:', e);
                console.error('DynamicFormRenderer: Invalid JSON:', formSchema);
                return null;
            }
        }
        
        console.log('DynamicFormRenderer: Using schema as object');
        return formSchema;
    }, [formSchema]);

    // Early return if no valid schema
    if (!parsedSchema) {
        return (
            <div className={styles.errorContainer}>
                <p className={styles.errorMessage}>No form schema available</p>
            </div>
        );
    }

    // Extract schema parts
    const schemaData = parsedSchema?.schema || parsedSchema;
    const fields = schemaData?.fields || [];
    const sections = schemaData?.sections || [];
    const layout = parsedSchema?.layout || { type: 'single-page' };
    const styling = parsedSchema?.styling || {};
    const behavior = parsedSchema?.behavior || {};
    const conditionalLogic = schemaData?.conditionalLogic || [];

    // Initialize form data with prefill
    useEffect(() => {
        if (Object.keys(prefillData).length > 0) {
            setFormData(prevData => ({
                ...prefillData,
                ...prevData
            }));
        }
    }, []);

    /**
     * Evaluate if a condition is met
     */
    const evaluateCondition = (condition) => {
        if (!condition) return false;
        
        const { field, equals, notEquals, in: inArray, contains, greaterThan, lessThan } = condition;
        const fieldValue = formData[field];
        
        // Equals check
        if (equals !== undefined) {
            return fieldValue === equals;
        }
        
        // Not equals check
        if (notEquals !== undefined) {
            return fieldValue !== notEquals;
        }
        
        // In array check
        if (inArray !== undefined && Array.isArray(inArray)) {
            return inArray.includes(fieldValue);
        }
        
        // Contains check (for arrays or strings)
        if (contains !== undefined) {
            if (Array.isArray(fieldValue)) {
                return fieldValue.includes(contains);
            }
            if (typeof fieldValue === 'string') {
                return fieldValue.includes(contains);
            }
        }
        
        // Greater than check
        if (greaterThan !== undefined) {
            const numValue = parseFloat(fieldValue);
            return !isNaN(numValue) && numValue > greaterThan;
        }
        
        // Less than check
        if (lessThan !== undefined) {
            const numValue = parseFloat(fieldValue);
            return !isNaN(numValue) && numValue < lessThan;
        }
        
        return false;
    };

    /**
     * Check if a section should be visible based on conditional logic
     */
    const isSectionVisible = (sectionId) => {
        for (const rule of conditionalLogic) {
            if (!rule.when || !rule.then) continue;
            
            const conditionMet = evaluateCondition(rule.when);
            
            // Check if this rule affects section visibility
            if (conditionMet) {
                if (rule.then.showSections && rule.then.showSections.includes(sectionId)) {
                    return true;
                }
                if (rule.then.hideSections && rule.then.hideSections.includes(sectionId)) {
                    return false;
                }
            } else {
                // Condition not met - hide sections that would be shown
                if (rule.then.showSections && rule.then.showSections.includes(sectionId)) {
                    return false;
                }
            }
        }
        
        return true; // Default to visible
    };

    /**
     * Check if a field should be visible based on conditional logic
     */
    const isFieldVisible = (fieldName) => {
        for (const rule of conditionalLogic) {
            if (!rule.when || !rule.then) continue;
            
            const conditionMet = evaluateCondition(rule.when);
            
            // If condition met, check if field should be shown/hidden
            if (conditionMet) {
                if (rule.then.show && rule.then.show.includes(fieldName)) {
                    return true;
                }
                if (rule.then.hide && rule.then.hide.includes(fieldName)) {
                    return false;
                }
            } else {
                // Condition not met - hide fields that would be shown
                if (rule.then.show && rule.then.show.includes(fieldName)) {
                    return false;
                }
            }
        }
        
        return true; // Default to visible
    };

    /**
     * Validate a single field
     */
    const validateField = (field, value) => {
        const errors = [];
        
        // Required validation
        if (field.required && !value) {
            errors.push(`${field.label} is required`);
        }
        
        // Skip other validations if field is empty and not required
        if (!value && !field.required) {
            return errors;
        }
        
        const validation = field.validation || {};
        
        // Min/Max length
        if (validation.minLength && value.length < validation.minLength) {
            errors.push(validation.errorMessage || `Minimum ${validation.minLength} characters required`);
        }
        if (validation.maxLength && value.length > validation.maxLength) {
            errors.push(validation.errorMessage || `Maximum ${validation.maxLength} characters allowed`);
        }
        
        // Min/Max value (for numbers)
        if (field.type === 'number') {
            const numValue = parseFloat(value);
            if (validation.min !== undefined && numValue < validation.min) {
                errors.push(validation.errorMessage || `Minimum value is ${validation.min}`);
            }
            if (validation.max !== undefined && numValue > validation.max) {
                errors.push(validation.errorMessage || `Maximum value is ${validation.max}`);
            }
        }
        
        // Pattern validation
        if (validation.pattern) {
            try {
                const regex = new RegExp(validation.pattern);
                if (!regex.test(value)) {
                    errors.push(validation.errorMessage || 'Invalid format');
                }
            } catch (e) {
                console.error('Invalid regex pattern:', validation.pattern);
            }
        }
        
        // Type-specific validation
        if (field.type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                errors.push('Please enter a valid email address');
            }
        }
        
        if (field.type === 'url' && value) {
            try {
                new URL(value);
            } catch {
                errors.push('Please enter a valid URL');
            }
        }
        
        return errors;
    };

    /**
     * Validate all fields
     */
    const validateAllFields = () => {
        const errors = {};
        
        fields.forEach(field => {
            // Skip validation for hidden fields
            if (!isFieldVisible(field.name)) {
                return;
            }
            
            const fieldErrors = validateField(field, formData[field.name]);
            if (fieldErrors.length > 0) {
                errors[field.name] = fieldErrors[0]; // Take first error
            }
        });
        
        return errors;
    };

    /**
     * Validate fields in current step only (for multi-step forms)
     */
    const validateCurrentStep = () => {
        if (layout.type !== 'multi-step' || sections.length === 0) {
            return validateAllFields();
        }
        
        const currentSection = sections[currentStep];
        if (!currentSection) return {};
        
        const errors = {};
        const sectionFieldNames = currentSection.fields || [];
        
        sectionFieldNames.forEach(fieldName => {
            const field = fields.find(f => f.name === fieldName);
            if (!field || !isFieldVisible(field.name)) return;
            
            const fieldErrors = validateField(field, formData[fieldName]);
            if (fieldErrors.length > 0) {
                errors[fieldName] = fieldErrors[0];
            }
        });
        
        return errors;
    };

    /**
     * Handle field change
     */
    const handleFieldChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
        
        // Validate on blur if enabled
        if (behavior.validateOnBlur && touchedFields[fieldName]) {
            const field = fields.find(f => f.name === fieldName);
            if (field) {
                const errors = validateField(field, value);
                setValidationErrors(prev => ({
                    ...prev,
                    [fieldName]: errors.length > 0 ? errors[0] : undefined
                }));
            }
        } else {
            // Clear error for this field
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    /**
     * Handle field blur
     */
    const handleFieldBlur = (fieldName) => {
        setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
        
        if (behavior.validateOnBlur) {
            const field = fields.find(f => f.name === fieldName);
            if (field) {
                const errors = validateField(field, formData[fieldName]);
                setValidationErrors(prev => ({
                    ...prev,
                    [fieldName]: errors.length > 0 ? errors[0] : undefined
                }));
            }
        }
    };

    /**
     * Get list of visible sections (for multi-step forms)
     */
    const getVisibleSections = () => {
        return sections.filter((section, index) => {
            if (!section.id) return true; // Always show sections without IDs
            return isSectionVisible(section.id);
        });
    };

    /**
     * Get next visible step index
     */
    const getNextVisibleStep = (currentIndex) => {
        for (let i = currentIndex + 1; i < sections.length; i++) {
            if (!sections[i].id || isSectionVisible(sections[i].id)) {
                return i;
            }
        }
        return currentIndex; // No next step
    };

    /**
     * Get previous visible step index
     */
    const getPreviousVisibleStep = (currentIndex) => {
        for (let i = currentIndex - 1; i >= 0; i--) {
            if (!sections[i].id || isSectionVisible(sections[i].id)) {
                return i;
            }
        }
        return currentIndex; // No previous step
    };

    /**
     * Handle next step in multi-step form
     */
    const handleNextStep = () => {
        const errors = validateCurrentStep();
        setValidationErrors(errors);
        
        if (Object.keys(errors).length === 0) {
            const nextStep = getNextVisibleStep(currentStep);
            if (nextStep !== currentStep) {
                setCurrentStep(nextStep);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    /**
     * Handle previous step in multi-step form
     */
    const handlePreviousStep = () => {
        const prevStep = getPreviousVisibleStep(currentStep);
        if (prevStep !== currentStep) {
            setCurrentStep(prevStep);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    /**
     * Handle form submission
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        
        const errors = validateAllFields();
        setValidationErrors(errors);
        
        if (Object.keys(errors).length === 0) {
            onSubmit(formData);
        } else {
            // Scroll to first error
            const firstErrorField = Object.keys(errors)[0];
            const element = document.getElementById(firstErrorField);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    /**
     * Render a section
     */
    const renderSection = (section, index) => {
        // Check if section itself should be visible
        if (section.id && !isSectionVisible(section.id)) {
            return null;
        }
        
        const sectionFieldNames = section.fields || [];
        const sectionFields = sectionFieldNames
            .map(fieldName => fields.find(f => f.name === fieldName))
            .filter(f => f && isFieldVisible(f.name));
        
        if (sectionFields.length === 0) return null;
        
        return (
            <div key={index} className={styles.section}>
                {section.title && (
                    <h3 className={styles.sectionTitle}>{section.title}</h3>
                )}
                {section.description && (
                    <p className={styles.sectionDescription}>{section.description}</p>
                )}
                <div className={styles.fieldsContainer}>
                    {sectionFields.map(field => (
                        <FormField
                            key={field.name}
                            field={field}
                            value={formData[field.name]}
                            error={validationErrors[field.name]}
                            onChange={handleFieldChange}
                            onBlur={handleFieldBlur}
                            disabled={submitting}
                        />
                    ))}
                </div>
            </div>
        );
    };

    /**
     * Render fields without sections
     */
    const renderFieldsWithoutSections = () => {
        const visibleFields = fields.filter(f => isFieldVisible(f.name));
        
        return (
            <div className={styles.section}>
                <div className={styles.fieldsContainer}>
                    {visibleFields.map(field => (
                        <FormField
                            key={field.name}
                            field={field}
                            value={formData[field.name]}
                            error={validationErrors[field.name]}
                            onChange={handleFieldChange}
                            onBlur={handleFieldBlur}
                            disabled={submitting}
                        />
                    ))}
                </div>
            </div>
        );
    };

    /**
     * Render progress indicator for multi-step forms
     */
    const renderProgressIndicator = () => {
        if (!layout.progressIndicator || sections.length <= 1) return null;
        
        const visibleSections = getVisibleSections();
        if (visibleSections.length <= 1) return null;
        
        const currentVisibleIndex = visibleSections.findIndex((_, idx) => {
            const originalIndex = sections.indexOf(visibleSections[idx]);
            return originalIndex === currentStep;
        });
        
        return (
            <div className={styles.progressIndicator}>
                <div className={styles.progressSteps}>
                    {visibleSections.map((section, visibleIndex) => {
                        const originalIndex = sections.indexOf(section);
                        return (
                            <div
                                key={originalIndex}
                                className={`${styles.progressStep} ${
                                    visibleIndex === currentVisibleIndex ? styles.progressStepActive : ''
                                } ${visibleIndex < currentVisibleIndex ? styles.progressStepCompleted : ''}`}
                            >
                                <div className={styles.progressStepNumber}>
                                    {visibleIndex < currentVisibleIndex ? '✓' : visibleIndex + 1}
                                </div>
                                <div className={styles.progressStepLabel}>
                                    {section.title || `Step ${visibleIndex + 1}`}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {behavior.showProgressBar && (
                    <div className={styles.progressBar}>
                        <div
                            className={styles.progressBarFill}
                            style={{
                                width: `${((currentVisibleIndex + 1) / visibleSections.length) * 100}%`,
                                backgroundColor: styling.primaryColor || '#0066cc'
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    if (!parsedSchema) {
        return (
            <div className={styles.error}>
                <h3>Invalid form schema</h3>
                <p>The form configuration could not be loaded. This may be because:</p>
                <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
                    <li>No form is associated with this link</li>
                    <li>The form schema is empty or null</li>
                    <li>The form schema contains invalid JSON</li>
                </ul>
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                    Please contact support for assistance.
                </p>
            </div>
        );
    }

    const isMultiStep = layout.type === 'multi-step' && sections.length > 1;
    const visibleSections = getVisibleSections();
    const currentSectionIndex = visibleSections.findIndex(s => sections.indexOf(s) === currentStep);
    const isLastStep = currentSectionIndex === visibleSections.length - 1;
    const isFirstStep = currentSectionIndex === 0;

    return (
        <div className={`${styles.formRenderer} ${className}`}>
            {renderProgressIndicator()}
            
            <form onSubmit={handleSubmit} className={styles.form}>
                {isMultiStep ? (
                    renderSection(sections[currentStep], currentStep)
                ) : sections.length > 0 ? (
                    sections.map((section, index) => renderSection(section, index))
                ) : (
                    renderFieldsWithoutSections()
                )}
                
                <div className={styles.actions}>
                    {isMultiStep && !isFirstStep && (
                        <button
                            type="button"
                            onClick={handlePreviousStep}
                            className={styles.buttonSecondary}
                            disabled={submitting}
                        >
                            Previous
                        </button>
                    )}
                    
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className={styles.buttonSecondary}
                            disabled={submitting}
                        >
                            {styling.cancelButtonText || 'Cancel'}
                        </button>
                    )}
                    
                    {isMultiStep && !isLastStep ? (
                        <button
                            type="button"
                            onClick={handleNextStep}
                            className={styles.buttonPrimary}
                            disabled={submitting}
                            style={{
                                backgroundColor: styling.primaryColor || '#0066cc'
                            }}
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            type="submit"
                            className={styles.buttonPrimary}
                            disabled={submitting}
                            style={{
                                backgroundColor: styling.primaryColor || '#0066cc'
                            }}
                        >
                            {submitting 
                                ? 'Submitting...' 
                                : styling.submitButtonText || 'Submit'
                            }
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

DynamicFormRenderer.propTypes = {
    formSchema: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]).isRequired,
    prefillData: PropTypes.object,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    submitting: PropTypes.bool,
    className: PropTypes.string
};

export default DynamicFormRenderer;
